// Function index:
//   mountCharacterSheet(opts) — wire the topbar Sheet button + overlay.
//       Renders the speaker's level/class/archetype/HP/XP/coin/stats/skills,
//       an equipped paperdoll strip, and inventory (icon per row) + the
//       party's faction standing; refetches on inventory.changed /
//       faction.updated and tracks live HP + level + XP + coin + skills.
//       Skills render as the curated 14 with rank + progress-to-next-rank
//       pips and a spend ("+") button while skill_points > 0
//       (POST .../skills/spend). Gear rows carry a self-serve equip/unequip
//       button (player.equip, refused mid-combat server-side); everything
//       else still mutates through the GM.

import {
  getInventory, getFactions, spendSkillPoint,
  type BootstrapCharacter, type InventoryItem, type FactionStanding,
} from '../api.js';
import { archetypeOf, ARCHETYPE_INFO, SKILL_NAMES } from '../game/archetypes.js';
import { iconForItem } from '../items/icon-map.js';
import type { WS } from '../ws.js';

const STAT_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const;
// Mirrors server/src/characters/skill-list.ts::RANK_CAP — ranks run 0..3.
const RANK_CAP = 3;

// Rank code -> the title an NPC would actually use.
const GUILD_TITLE: Record<string, string> = {
  E: 'Novice', D: 'Apprentice', C: 'Journeyman', B: 'Veteran', A: 'Elite', S: 'Legend',
};

export function mountCharacterSheet(opts: { character: BootstrapCharacter; partyId: number; ws: WS }): void {
  const { character, partyId, ws } = opts;
  const btn = document.getElementById('sheet-btn') as HTMLButtonElement;
  const overlay = document.getElementById('sheet-overlay') as HTMLDivElement;
  const close = document.getElementById('sheet-close') as HTMLButtonElement;
  const body = document.getElementById('sheet-body') as HTMLDivElement;

  btn.hidden = false;

  let hp = character.hp;
  let maxHp = character.max_hp;
  let level = character.level;
  let xp = character.xp;
  let coin = character.coin;
  let skills: Record<string, number> = { ...character.skills };
  let skillPoints = character.skill_points;
  const skillProgress: Record<string, unknown> = character.skill_progress ?? {};
  let skillError: string | null = null;
  let inventory: InventoryItem[] = [];
  let factions: FactionStanding[] = [];
  let open = false;

  function render(): void {
    const c = character;
    const out: string[] = [];
    out.push(`<div class="sheet-name">${esc(c.name)}</div>`);
    const sub = [`Level ${level}`];
    if (c.character_class) sub.push(esc(c.character_class));
    out.push(`<div class="sheet-sub">${sub.join(' · ')}</div>`);
    // Archetype is DERIVED from character_class, never stored — same
    // client-side keyword map the creation-time preview uses (game/archetypes.js).
    const archetype = archetypeOf(c.character_class);
    const info = ARCHETYPE_INFO[archetype];
    const archBits = [info.packageText, info.signature].filter(Boolean).join(' · ');
    out.push(`<div class="sheet-sub sheet-archetype">${esc(info.label)}${archBits ? ` — ${esc(archBits)}` : ''}</div>`);
    const low = hp === 0 || hp <= maxHp * 0.33 ? ' low' : '';
    out.push(`<div class="sheet-hp${low}">HP ${hp} / ${maxHp}</div>`);
    out.push(`<div class="sheet-sub">${xp} XP · ${coin} coin</div>`);
    // Guild standing — a second, social progression axis. Shown only once
    // there is something to show; a civilian with no Merit says nothing.
    if (c.guild_rank && c.guild_rank !== 'none') {
      out.push(`<div class="sheet-sub sheet-guild">${esc(GUILD_TITLE[c.guild_rank] ?? c.guild_rank)} · ${Number(c.merit ?? 0)} Merit</div>`);
    } else if (Number(c.merit ?? 0) > 0) {
      out.push(`<div class="sheet-sub sheet-guild">Unaffiliated · ${Number(c.merit)} Merit</div>`);
    }

    const hasStats = STAT_KEYS.some((k) => typeof c.stats?.[k] === 'number');
    if (hasStats) {
      out.push('<h3>Ability scores</h3><div class="stat-grid">');
      for (const k of STAT_KEYS) {
        out.push(`<div class="stat-cell"><span class="k">${k}</span><span class="v">${c.stats[k] ?? 10}</span></div>`);
      }
      out.push('</div>');
    }

    out.push('<h3>Skills</h3>');
    if (skillPoints > 0) {
      out.push(`<div class="sheet-sub">${skillPoints} skill point${skillPoints === 1 ? '' : 's'} to spend</div>`);
    }
    if (skillError) out.push(`<div class="sheet-sub sheet-skill-error">${esc(skillError)}</div>`);
    out.push('<div class="skill-list">');
    for (const name of SKILL_NAMES) {
      const rank = skills[name] ?? 0;
      const entry = skillProgress[name] as { ticks?: number } | undefined;
      const ticks = typeof entry?.ticks === 'number' ? entry.ticks : 0;
      // Pips show progress toward the NEXT rank (ticks % RANK_CAP of
      // RANK_CAP — the server ranks up at cumulative ticks 3/6/9). A
      // capped skill has no next rank to chase, so it always reads full.
      const pipsFilled = rank >= RANK_CAP ? RANK_CAP : ticks % RANK_CAP;
      const pips = Array.from({ length: RANK_CAP },
        (_, i) => `<span class="skill-pip${i < pipsFilled ? ' filled' : ''}"></span>`).join('');
      const canSpend = skillPoints > 0 && rank < RANK_CAP;
      const spend = canSpend
        ? `<button class="secondary skill-spend-btn" data-skill="${esc(name)}">+</button>`
        : '';
      out.push(
        `<div class="skill-line" data-skill="${esc(name)}"><span class="skill-name">${esc(name)}</span>`
        + `<span class="skill-rank">rank ${rank}</span>`
        + `<span class="skill-pips">${pips}</span>${spend}</div>`,
      );
    }
    out.push('</div>');

    out.push('<h3>Equipped</h3><div class="equip-strip">');
    const equipped = inventory.filter((i) => i.equipped);
    const bySlot = new Map<string, InventoryItem[]>();
    for (const it of equipped) {
      const s = it.slot ?? 'equipped';
      (bySlot.get(s) ?? bySlot.set(s, []).get(s)!).push(it);
    }
    const slots = ['mainhand', 'offhand', 'armor', ...[...bySlot.keys()].filter((s) => !['mainhand', 'offhand', 'armor'].includes(s))];
    for (const s of slots) {
      const items = bySlot.get(s) ?? [];
      if (items.length === 0) {
        out.push(`<div class="slot-box empty" data-slot="${esc(s)}"><span class="slot-label">${esc(s)}</span></div>`);
      } else {
        for (const it of items) { // defensive: render dupes rather than hide (spec error table)
          out.push(`<div class="slot-box" data-slot="${esc(s)}" title="${esc(it.name)} (${esc(it.kind)})">`
            + `<span class="item-icon" style="--icon:url('${iconForItem(it)}')"></span>`
            + `<span class="slot-item">${esc(it.name)}</span></div>`);
        }
      }
    }
    out.push('</div>');

    out.push('<h3>Inventory</h3>');
    if (inventory.length === 0) {
      out.push('<div class="sheet-empty">Nothing carried yet.</div>');
    } else {
      for (const it of inventory) {
        const eq = it.equipped ? `<span class="eq">${esc(it.slot ?? 'equipped')}</span>` : '';
        const qty = it.qty > 1 ? `<span class="qty">×${it.qty}</span>` : '';
        // Gear is self-serve: anything wearable/wieldable gets the toggle so
        // looted equipment never waits on the model calling equip_item.
        const gear = it.kind === 'weapon' || it.kind === 'armor' || it.props['slot'] !== undefined;
        const act = gear
          ? `<button class="secondary equip-btn" data-name="${esc(it.name)}" data-on="${it.equipped ? '0' : '1'}">${it.equipped ? 'unequip' : 'equip'}</button>`
          : '';
        out.push(`<div class="inv-row"><span class="item-icon inv" style="--icon:url('${iconForItem(it)}')"></span><span class="nm">${esc(it.name)}</span>${qty}${eq}<span class="kind">${esc(it.kind)}</span>${act}</div>`);
      }
    }

    if (factions.length > 0) {
      out.push('<h3>Reputation</h3>');
      for (const f of factions) {
        const sign = f.standing >= 0 ? '+' : '';
        out.push(`<div class="inv-row"><span class="nm">${esc(f.name)}</span><span class="qty">${esc(f.label)}</span><span class="kind">${sign}${f.standing}</span></div>`);
      }
    }
    body.innerHTML = out.join('');
  }

  async function refreshData(): Promise<void> {
    try {
      const [inv, fac] = await Promise.all([getInventory(character.id), getFactions(partyId)]);
      inventory = inv.inventory;
      factions = fac.factions;
    } catch { /* keep previous on transient failure */ }
    if (open) render();
  }

  btn.addEventListener('click', () => {
    open = true;
    overlay.hidden = false;
    render();
    void refreshData();
  });
  // Delegated: rows re-render wholesale, so the handlers live on the body.
  // Equip outcome arrives as inventory.changed (refresh) + a private system
  // line; the server refuses mid-combat re-gearing. Skill spend is a direct
  // HTTP round-trip (not a ws.send fire-and-forget) since the response
  // carries the new rank + skill_points the render needs immediately.
  body.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const equipEl = target.closest('.equip-btn') as HTMLButtonElement | null;
    if (equipEl && equipEl.dataset.name) {
      ws.send('player.equip', { item_name: equipEl.dataset.name, equipped: equipEl.dataset.on === '1' });
      return;
    }
    const spendEl = target.closest('.skill-spend-btn') as HTMLButtonElement | null;
    if (spendEl && spendEl.dataset.skill) void spendSkill(spendEl.dataset.skill, spendEl);
  });

  async function spendSkill(skill: string, el: HTMLButtonElement): Promise<void> {
    el.disabled = true; // guard a double-click while the request is in flight
    try {
      const res = await spendSkillPoint(character.id, skill);
      skills = { ...skills, [res.skill]: res.rank };
      skillPoints = res.skill_points;
      skillError = null;
    } catch (err) {
      skillError = (err as Error).message;
    }
    if (open) render();
  }
  const hide = (): void => { open = false; overlay.hidden = true; };
  close.addEventListener('click', hide);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });

  ws.on('inventory.changed', (m: { character_id: number }) => {
    if (m.character_id === character.id) void refreshData();
  });
  ws.on('faction.updated', () => { if (open) void refreshData(); });
  ws.on('character.hp_changed', (m: { character_id: number; hp: number; max_hp: number }) => {
    if (m.character_id !== character.id) return;
    hp = m.hp; maxHp = m.max_hp;
    if (open) render();
  });
  ws.on('character.leveled', (m: { character_id: number; level: number; xp: number; hp: number; max_hp: number }) => {
    if (m.character_id !== character.id) return;
    level = m.level; xp = m.xp; hp = m.hp; maxHp = m.max_hp;
    if (open) render();
  });
  // Kill XP and quest pay move the number between level-ups too.
  ws.on('character.xp_changed', (m: { character_id: number; xp: number; level: number }) => {
    if (m.character_id !== character.id) return;
    xp = m.xp; level = m.level;
    if (open) render();
  });
  // Coin moves via shop buy/sell and GM grants alike — mirror xp_changed.
  ws.on('character.coin_changed', (m: { character_id: number; coin: number }) => {
    if (m.character_id !== character.id) return;
    coin = m.coin;
    if (open) render();
  });
  // Learn-by-doing ranks a skill without a spend — the event carries only
  // the one skill's new rank (not skill_points, which this event never
  // touches: it's the OTHER faucet). A deliberate spend already updates
  // local state from its own HTTP response; this echo is a harmless re-set.
  ws.on('character.skills_changed', (m: { character_id: number; skill: string; rank: number }) => {
    if (m.character_id !== character.id) return;
    skills = { ...skills, [m.skill]: m.rank };
    if (open) render();
  });
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
